import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, Save, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { SingleUploader } from '../../components/MediaUploader';
import api, { resolveMedia } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const EMPTY = { name: '', tagline: '', logo: '', order: 0 };

export default function AdminDevelopers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = () => api.get('/admin/developers').then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const open = (item) => { setEditing(item || 'new'); setForm(item || { ...EMPTY, order: items.length + 1 }); };
  const close = () => { setEditing(null); setForm(EMPTY); };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing && editing !== 'new') await api.put('/admin/developers/' + editing.id, payload);
      else await api.post('/admin/developers', payload);
      toast({ title: 'Saved' });
      close(); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this developer?')) return;
    await api.delete('/admin/developers/' + id);
    toast({ title: 'Deleted' });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-neutral-900">Developers</h1><p className="text-sm text-neutral-500">Featured partner developers shown on homepage</p></div>
        <Button onClick={() => open(null)} className="btn-primary gap-2"><Plus className="w-4 h-4" /> Add Developer</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <div key={d.id} className="bg-white p-5 rounded-xl border border-neutral-200 flex flex-col items-center text-center">
            <img src={resolveMedia(d.logo) || '/assets/logo.png'} alt={d.name} className="h-20 w-auto object-contain mb-3" />
            <p className="font-bold text-neutral-900">{d.name}</p>
            <p className="text-xs text-neutral-500">{d.tagline}</p>
            <span className="text-xs text-neutral-400 mt-1">Order: {d.order}</span>
            <div className="flex gap-1 mt-3">
              <Button size="sm" variant="outline" onClick={() => open(d)} className="gap-1"><Edit className="w-3.5 h-3.5" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => remove(d.id)} className="gap-1 text-red-600 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-neutral-900">{editing === 'new' ? 'New Developer' : 'Edit Developer'}</h2>
              <button onClick={close}><X className="w-5 h-5 text-neutral-500" /></button>
            </div>
            <div className="space-y-4">
              <SingleUploader value={form.logo} onChange={(v) => setForm(f => ({...f, logo: v}))} label="Logo" />
              <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Name *</label><Input required value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} /></div>
              <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Tagline</label><Input value={form.tagline} onChange={(e) => setForm(f => ({...f, tagline: e.target.value}))} /></div>
              <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Order</label><Input type="number" value={form.order} onChange={(e) => setForm(f => ({...f, order: e.target.value}))} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="btn-primary gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
